import { Operators } from '../../../../data';
import { OfferCost, ResiliationFormulaState, TerminationFees } from '../types';
import { computeRemainingMonths } from './utils';

function computeLaw(state: ResiliationFormulaState): [string, number] {
  if (state.commitmentDuration === 0) {
    return [
      'Contrat sans engagement de durée',
      0,
    ];
  }
  if (state.commitmentDuration === 12) {
    return [
      "Contrat avec engagement de 12 mois : L'abonné doit payer l’intégralité des mensualités restent dues.",
      0,
    ];
  }
  if (state.startDate?.getFullYear() === 2022) {
    return [
      'Souscription validée avant le 1er janvier 2023 : Contrat avec engagement de 24 mois : l’abonné doit payer l’intégralité des mensualités restant dues sur la première année, puis 25% des mensualités de la 2nde année.',
      0.25,
    ];
  }
  if (state.hasEquipment) {
    return [
      'Souscription validée après le 1er janvier 2023 : Contrat avec engagement de 24 mois comprenant la vente d’un équipement subventionné (téléphone, télévision, console de jeux…) : l’abonné doit payer l’intégralité des mensualités restant dues sur la première année, puis 20% des mensualités de la 2nde année.',
      0.2,
    ];
  }
  return [
    'Souscription validée après le 1er janvier 2023 : Contrat avec engagement de 24 mois ne comprenant pas la vente d’un équipement subventionné : L’abonné doit payer l’intégralité des mensualités restant dues sur la première année, puis n’est pas redevable des sommes dues au titre de la période d’engagement restant à courir de la 2nde année.',
    0,
  ];
}

function computeFirstYear(commitmentDuration: 0 | 12 | 24, normalPrice: number, startDate?: Date): OfferCost | undefined {
  if (commitmentDuration === 0) return undefined;
  if (!startDate) throw new Error('Missing start date');

  const leftoverMonths = computeRemainingMonths(commitmentDuration, startDate);
  if (leftoverMonths === undefined) return undefined;
  let firstYearMonths = leftoverMonths;

  if (commitmentDuration === 24) {
    firstYearMonths = Math.max(0, firstYearMonths - 12);
  }

  if (firstYearMonths === 0) { return undefined; }
  return {
    total: firstYearMonths * normalPrice,
    months: firstYearMonths,
    monthly: normalPrice,
  };
}

function computeSecondYear(commitmentDuration: 0 | 12 | 24, normalPrice: number, ratio: number, startDate?: Date): OfferCost & { ratio: number; } | undefined {
  if (commitmentDuration === 0 || ratio === 0) return undefined;
  if (!startDate) throw new Error('Missing start date');

  const leftoverMonths = computeRemainingMonths(commitmentDuration, startDate);
  if (leftoverMonths === undefined) return undefined;
  let firstYearMonths = leftoverMonths;
  let secondYearMonths = 0;

  if (commitmentDuration === 24) {
    firstYearMonths = Math.max(0, firstYearMonths - 12);
    secondYearMonths = leftoverMonths - firstYearMonths;
  }

  if (secondYearMonths === 0) return undefined;

  return {
    total: secondYearMonths * normalPrice * ratio,
    months: secondYearMonths,
    monthly: normalPrice,
    ratio,
  };
}

export default function computeTerminationFees(state: ResiliationFormulaState): TerminationFees | undefined {
  if (state.contractType === undefined) return undefined;
  if (state.contractType === 'box' && state.operator === undefined) return undefined;
  if (state.commitmentDuration === undefined) return undefined;
  if (state.promotionDuration === undefined) return undefined;
  if (state.commitmentDuration !== 0 && state.startDate === undefined) return undefined;
  if (state.promotionDuration !== 0 && state.startDate === undefined) return undefined;
  if (state.normalPrice === undefined) return undefined;

  const [law, secondYearRatio] = computeLaw(state);

  const firstYear = computeFirstYear(state.commitmentDuration, state.normalPrice, state.startDate);
  const secondYear = computeSecondYear(state.commitmentDuration, state.normalPrice, secondYearRatio, state.startDate);
  const boxTerminationFee = Operators.find((o) => o.name === state.operator)?.closingFee;
  const amountToPay = (firstYear?.total ?? 0) + (secondYear?.total ?? 0) + (boxTerminationFee ?? 0);
  const reimbursement = boxTerminationFee ? Math.min(100, amountToPay) : undefined;
  const amountAfterReimbursement = reimbursement ? amountToPay - reimbursement : undefined;

  return {
    law,
    firstYear,
    secondYear,
    boxTerminationFee,
    amountToPay,
    reimbursement,
    amountAfterReimbursement,
  };
}
